import { FC, useMemo } from 'react';
import { Flex, Select, Switch, Text, TextInput, Link } from '@gravity-ui/uikit';
import { ProfilesRecord } from '../../Page/types/ProfilesRecord';
import { MAX_VIDEO_TITLE_LENGTH } from '../../../constants/MAX_VIDEO_TITLE_LENGTH';
import { QuestionIconPopover } from '../../ui/QuestionIconPopover/QuestionIconPopover';
import { getDefaultProfile } from '../../../libs/utils/getProfileOptions';
import { ThumbnailUpload, ThumbnailChange } from '../../ThumbnailUpload/ThumbnailUpload';
import { formsT } from '../../../i18n';

type MobileVideoInfoFormProps = {
    profile: string;
    profiles: ProfilesRecord;
    name: string;
    enableSummarization: boolean;
    placeholder?: string;
    onChangeName: (value: string) => void;
    onChangeProfile: (value: string) => void;
    onToggleSummarization: () => void;
    disabled: boolean;
    thumbnailProps?: {
        currentThumbnailId?: string;
        onChange: (change: ThumbnailChange) => void;
    };
};

export const MobileVideoInfoForm: FC<MobileVideoInfoFormProps> = ({
    profile,
    profiles,
    name,
    enableSummarization,
    placeholder = '',
    onChangeName,
    onChangeProfile,
    onToggleSummarization,
    disabled,
    thumbnailProps,
}) => {
    const currentProfile = profile || getDefaultProfile(profiles);

    const profileOptions = useMemo(
        () =>
            Object.entries(profiles).map(([key, val]) => ({
                value: key,
                content: val.description || key,
                disabled: val.disabled,
            })),
        [profiles],
    );

    const titleError = useMemo(() => {
        if (!name.length && !placeholder) return formsT('titleCannotBeEmpty');
        if (name.length > MAX_VIDEO_TITLE_LENGTH)
            return formsT('titleMaxLength', { maxLength: MAX_VIDEO_TITLE_LENGTH });
        return undefined;
    }, [name, placeholder]);

    return (
        <Flex direction="column" gap={5} className="mobile-video-info-form">
            {/* Профиль */}
            <Flex direction="column" gap={2}>
                <Flex alignItems="center" gap={1}>
                    <Text variant="subheader-2">
                        {formsT('profile')}
                        <span style={{ color: 'var(--g-color-text-danger)' }}> *</span>
                    </Text>
                    <QuestionIconPopover
                        popoverContent={
                            <Link
                                href="https://wiki.yandex-team.ru/vp/users/nda-video/#profili"
                                target="_blank"
                            >
                                {formsT('profilesInfo')}
                            </Link>
                        }
                        style={{
                            position: 'relative',
                            top: '4px',
                        }}
                    />
                </Flex>
                <Select
                    size="xl"
                    value={[currentProfile]}
                    options={profileOptions}
                    onUpdate={(val) => onChangeProfile(val[0])}
                    disabled={disabled}
                    width="max"
                />
            </Flex>

            {/* Название */}
            <Flex direction="column" gap={2}>
                <Text variant="subheader-2">
                    {formsT('title')}
                    <span style={{ color: 'var(--g-color-text-danger)' }}> *</span>
                </Text>
                <TextInput
                    size="xl"
                    value={name}
                    placeholder={placeholder}
                    onUpdate={onChangeName}
                    validationState={name.length > MAX_VIDEO_TITLE_LENGTH ? 'invalid' : undefined}
                    error={titleError}
                    errorMessage={titleError}
                    errorPlacement="outside"
                    disabled={disabled}
                />
            </Flex>

            {/* Суммаризация */}
            <Flex justifyContent="space-between" alignItems="center" gap={3}>
                <Text variant="subheader-2">{formsT('summarization')}</Text>
                <Switch
                    size="l"
                    checked={enableSummarization}
                    onChange={onToggleSummarization}
                    disabled={disabled}
                />
            </Flex>

            {/* Превью */}
            {thumbnailProps && (
                <Flex direction="column" gap={2}>
                    <Text variant="subheader-2">{formsT('thumbnail')}</Text>
                    <ThumbnailUpload
                        currentThumbnailId={thumbnailProps.currentThumbnailId}
                        onChange={thumbnailProps.onChange}
                        disabled={disabled}
                        compact
                        hideLabel
                    />
                </Flex>
            )}
        </Flex>
    );
};
